import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { Button, Input, Label, Card, CardHeader, CardTitle, CardContent } from '../components/ui';
import { Lock, Mail, Eye, EyeOff, Loader, ShoppingBag } from 'lucide-react';

/**
 * LoginPage Component
 * 
 * Authentication page with email/password login.
 * Supports remember me and password reset.
 *
 * @param {Function} onLoginSuccess - Callback when login succeeds
 * @returns {JSX.Element} Login page component
 */
export default function LoginPage({ onLoginSuccess }) {
  const toast = useToast();
  const { signIn, isLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true,
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Validate form
  const validate = useCallback(() => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'ელ-ფოსტა აუცილებელია';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'არასწორი ელ-ფოსტის ფორმატი';
    }

    if (!formData.password) {
      newErrors.password = 'პაროლი აუცილებელია';
    } else if (formData.password.length < 6) {
      newErrors.password = 'პაროლი უნდა იყოს მინიმუმ 6 სიმბოლო';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  // Handle submit
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await signIn(formData.email, formData.password, formData.rememberMe);
      if (onLoginSuccess) onLoginSuccess();
    } catch (error) {
      // Error handled in useAuth hook
    }
  }, [formData, validate, signIn, onLoginSuccess]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6 relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-pink-400 via-purple-500 via-blue-500 to-cyan-400 animate-[gradient-shift_8s_ease_infinite]">
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        {/* Floating shapes */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-pink-300/30 rounded-full blur-2xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-300/30 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-blue-300/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <Card className={`w-full max-w-md shadow-2xl glass border-0 relative z-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <CardHeader className="space-y-4 text-center pt-8">
          {/* Shopping bag icon with gradient */}
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/30 transform hover:scale-105 transition-transform duration-300">
            <ShoppingBag className="h-10 w-10 text-white" />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold text-gradient">Deals Manager</CardTitle>
            <p className="text-muted-foreground mt-2 text-sm">
              შესვლა სისტემაში
            </p>
          </div>
        </CardHeader>

        <CardContent className="px-6 pb-8">
          <form onSubmit={handleSubmit} className="space-y-5" aria-label="შესვლის ფორმა">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                <Mail className="inline h-4 w-4 mr-1.5" />
                ელ-ფოსტა
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                placeholder="ketokhachapuridze@gmail.com"
                autoFocus
                aria-required="true"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className="h-14 rounded-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur border-gray-200/50 dark:border-gray-600/50 focus:border-pink-400 focus:ring-pink-200 transition-all duration-200 shadow-sm focus:shadow-md"
              />
              {errors.email && (
                <p id="email-error" role="alert" className="text-red-500 text-xs">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">პაროლი</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  placeholder="••••••••"
                  aria-required="true"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  className="h-14 pr-12 rounded-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur border-gray-200/50 dark:border-gray-600/50 focus:border-pink-400 focus:ring-pink-200 transition-all duration-200 shadow-sm focus:shadow-md"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 text-gray-400 hover:text-pink-500 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'დამალე პაროლი' : 'აჩვენე პაროლი'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </Button>
              </div>
              {errors.password && (
                <p id="password-error" role="alert" className="text-red-500 text-xs">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={formData.rememberMe}
                onChange={(e) => handleFieldChange('rememberMe', e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-pink-500 focus:ring-pink-300 cursor-pointer"
              />
              <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer text-gray-600 dark:text-gray-300">
                დამიმახსოვრე (1 თვე)
              </Label>
            </div>

            {/* Submit Button with gradient */}
            <Button
              type="submit"
              className="w-full h-14 text-base font-semibold rounded-xl bg-gradient-to-r from-pink-500 via-rose-500 to-purple-600 text-white shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader className="h-5 w-5 mr-2 animate-spin" />
                  შესვლა...
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5 mr-2" />
                  შესვლა
                </>
              )}
            </Button>

            {/* Help Text */}
            <p className="text-xs text-center text-gray-500">
              შესვლით თქვენ ეთანხმებით მომსახურების პირობებს
            </p>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200/50 dark:border-gray-700/50">
            <p className="text-xs text-center text-gray-400 dark:text-gray-500">
              Deals Manager v3.0.0 • ნივთების მართვის სისტემა
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

LoginPage.propTypes = {
  onLoginSuccess: PropTypes.func,
};